import * as React from 'react';

import { useDebounceFn } from 'ahooks';
import { Col, Form, Input, Row } from 'antd';

import { AntdUploadImgCrop } from 'components';
import { useUserAction, useUserValue } from 'context';
import { generateDownloadFile, inputRule, PATHNAME } from 'utils';

const {
  QUERY_PARAM: {
    DEFAULT: { AT_SIGN: DEFAULT_AT_SIGN },
  },
} = PATHNAME;

type FieldType = Pick<
  ReturnType<typeof useUserValue>,
  | 'avatarId'
  | 'firstName'
  | 'lastName'
  | 'displayName'
  | 'email'
  | 'phoneNumber'
  | 'telNumber'
  | 'userName'
>;

const UserFormContent = () => {
  const {
    avatarId,
    firstName = '',
    lastName,
    displayName,
    userName,
    phoneNumber,
    telNumber,
    email,
    ownerType,
  } = useUserValue();
  const action = useUserAction();

  const { run: debounceUpdateFirstName } = useDebounceFn(
    (debouncedFirstName: typeof firstName) => {
      action({
        type: 'UPDATE_USER_FIRST_NAME',
        payload: {
          firstName: debouncedFirstName,
        },
      });
    },
  );
  const { run: debounceUpdateDisplayName } = useDebounceFn(
    (debouncedDisplayName: typeof displayName) => {
      action({
        type: 'UPDATE_USER_DISPLAY_NAME',
        payload: {
          displayName: debouncedDisplayName,
        },
      });
    },
  );

  if (!ownerType) return <React.Fragment />;

  return (
    <Row className='px-6 py-8'>
      <Col span={24}>
        <Form.Item<FieldType>
          label='چهرک (Avatar)'
          name='avatarId'
          valuePropName='file'
        >
          <AntdUploadImgCrop
            rootConfig={{
              cropShape: 'round',
            }}
            ownerType={ownerType}
            fileList={
              avatarId
                ? [
                    {
                      uid: avatarId,
                      name: firstName,
                      status: 'done',
                      url: generateDownloadFile(ownerType, avatarId),
                    },
                  ]
                : undefined
            }
            onChange={({ file: { status, response } }) => {
              action({
                type: 'UPDATE_USER_AVATAR',
                payload: {
                  avatarId: undefined,
                },
              });

              if (status === 'done' && response) {
                const { id } = response;
                action({
                  type: 'UPDATE_USER_AVATAR',
                  payload: {
                    avatarId: id,
                  },
                });
              }
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='نام'
          name='firstName'
          rules={[inputRule.required, inputRule.string]}
        >
          <Input
            placeholder='نام کاربر را وارد کنید'
            onChange={({ target: { value } }) => {
              debounceUpdateFirstName(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='نام خانوادگی'
          name='lastName'
          rules={[inputRule.string]}
          initialValue={lastName}
        >
          <Input placeholder='نام خانوادگی کاربر را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='نام نمایشی'
          name='displayName'
          rules={[inputRule.string]}
        >
          <Input
            placeholder='نام نمایشی کاربر را وارد کنید'
            onChange={({ target: { value } }) => {
              debounceUpdateDisplayName(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='ایمیل'
          name='email'
          rules={[inputRule.email]}
          initialValue={email}
        >
          <Input placeholder='ایمیل کاربر را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='شماره همراه'
          name='phoneNumber'
          rules={[inputRule.required, inputRule.phoneNumber]}
          initialValue={phoneNumber}
        >
          <Input placeholder='شماره همراه کاربر را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='شماره ثابت'
          name='telNumber'
          rules={[inputRule.telNumber]}
          initialValue={telNumber}
        >
          <Input placeholder='شماره ثابت کاربر را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='نام کاربری'
          name='userName'
          rules={[inputRule.userName]}
          initialValue={userName}
        >
          <Input
            placeholder='نام کاربری کاربر را وارد کنید'
            addonAfter={DEFAULT_AT_SIGN}
            classNames={{
              input: '[&:not([value=""])]:[direction:ltr]',
            }}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { UserFormContent };
